import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { UserPlus, Loader2, AlertCircle, RefreshCw, Send, Copy, Check, XCircle } from "lucide-react";

/**
 * v23.24 — Bulk managed-driver invite panel.
 * Paste one row per driver — "Name, email@example.com" or just "email@example.com".
 * Each recipient gets a magic-link email that auto-provisions them as an
 * auto-verified managed driver on acceptance.
 */
export default function AdminBulkInvitePanel() {
  const [raw, setRaw] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [lastResult, setLastResult] = React.useState(null);
  const [invites, setInvites] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("pending");
  const [copiedId, setCopiedId] = React.useState("");
  const [linkFor, setLinkFor] = React.useState(null); // {id, url}
  const [copiedResult, setCopiedResult] = React.useState("");

  const loadInvites = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/admin/managed-invites?status=${filter}`);
      setInvites(data.invites || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load invites");
    } finally { setLoading(false); }
  }, [filter]);

  React.useEffect(() => { loadInvites(); }, [loadInvites]);

  // Parse "Alice Smith, alice@x.com" or just "alice@x.com" per line
  const parseRows = (text) => {
    const rows = [];
    const seen = new Set();
    for (const line of text.split(/\r?\n/)) {
      const s = line.trim();
      if (!s) continue;
      const emailMatch = s.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
      if (!emailMatch) continue;
      const email = emailMatch[0].toLowerCase();
      if (seen.has(email)) continue;
      seen.add(email);
      // Everything before the email (comma-separated) is treated as name
      const before = s.slice(0, emailMatch.index).trim().replace(/[,;]+$/, "").trim();
      rows.push({ email, name: before || null });
    }
    return rows;
  };
  const parsed = parseRows(raw);

  const submit = async () => {
    if (parsed.length === 0) return;
    setSubmitting(true); setError(""); setLastResult(null);
    try {
      const { data } = await apiClient.post("/admin/managed-invites", { invites: parsed });
      setLastResult(data);
      setRaw("");
      await loadInvites();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send invites");
    } finally { setSubmitting(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite? The magic link will stop working.")) return;
    try {
      await apiClient.post(`/admin/managed-invites/${id}/revoke`);
      await loadInvites();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to revoke");
    }
  };

  const resend = async (id) => {
    try {
      const { data } = await apiClient.post(`/admin/managed-invites/${id}/resend`);
      // If the browser exposes the clipboard, copy the fresh link so the admin
      // can hand it over via WhatsApp/text if email delivery is unreliable.
      if (data?.accept_url && navigator.clipboard) {
        try { await navigator.clipboard.writeText(data.accept_url); } catch { /* ignore */ }
      }
      setLinkFor({ id, url: data?.accept_url || "" });
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 2500);
      await loadInvites();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to resend");
    }
  };

  const copyRow = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch { /* clipboard locked down */ }
  };

  const badgeCls = (status) =>
    ({
      pending: "text-[#A66800] bg-[#F5A623]/10 border-[#F5A623]/40",
      accepted: "text-[#2c8642] bg-[#5BB247]/10 border-[#5BB247]/40",
      revoked: "text-[#B91C1C] bg-[#FEE2E2] border-[#FCA5A5]",
      expired: "text-[#475569] bg-[#F5F5F5] border-[#0A0A0A]/15",
    }[status] || "text-[#475569] bg-white border-[#0A0A0A]/15");

  return (
    <section className="mb-12" data-testid="admin-bulk-invite-panel">
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] inline-flex items-center gap-2">
            <UserPlus size={20} /> Bulk invite Raanah team
          </h2>
          <p className="mt-1 text-sm text-[#475569] max-w-2xl">
            Paste one driver per line. Each recipient gets a magic link — no self-signup, no doc uploads. Format: <code className="font-mono text-[11px] bg-white px-1 py-0.5 border border-[#0A0A0A]/10">Name, email@example.com</code> or just the email.
          </p>
          <p className="mt-2 text-[11px] text-[#475569] max-w-2xl" data-testid="admin-invite-fallback-hint">
            If Gmail/Outlook drops the email, use <strong>Copy link &amp; resend</strong> on any pending row to grab a fresh invite URL and send it over WhatsApp/SMS.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="admin-invite-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
            Paste your team ({parsed.length} valid {parsed.length === 1 ? "row" : "rows"})
          </label>
          <textarea
            rows={7}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={"Alice Smith, alice@raanahstaff.co.uk\nBob Jones, bob@raanahstaff.co.uk\ncharlie@raanahstaff.co.uk"}
            data-testid="admin-invite-textarea"
            className="w-full px-3 py-2 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm font-mono"
          />
          <button
            type="button"
            onClick={submit}
            disabled={submitting || parsed.length === 0}
            data-testid="admin-invite-submit"
            className="mt-3 inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2 text-sm font-semibold disabled:opacity-60"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send {parsed.length || ""} invite{parsed.length === 1 ? "" : "s"}
          </button>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">Preview</p>
          <div className="bg-white border border-[#0A0A0A]/10 divide-y divide-[#0A0A0A]/5 max-h-[210px] overflow-auto" data-testid="admin-invite-preview">
            {parsed.length === 0 ? (
              <p className="text-xs text-[#475569] p-3">Nothing to send yet.</p>
            ) : parsed.map((r) => (
              <div key={r.email} className="p-2 text-xs flex items-center justify-between gap-3">
                <div className="min-w-0 truncate">
                  <span className="font-semibold text-[#0A0A0A]">{r.name || "—"}</span>
                  <span className="text-[#475569]"> · {r.email}</span>
                </div>
              </div>
            ))}
          </div>
          {lastResult && (
            <div className="mt-3 bg-[#5BB247]/5 border border-[#5BB247]/30 p-3 text-xs" data-testid="admin-invite-last-result">
              <p className="font-semibold text-[#2c8642]">
                {lastResult.count} row{lastResult.count === 1 ? "" : "s"} processed. Links valid {lastResult.expires_in_days} days.
              </p>
              <p className="mt-1 text-[11px] text-[#475569]">
                An email has been queued. If it doesn&apos;t arrive, copy the invite link and share it directly (WhatsApp / SMS).
              </p>
              <ul className="mt-1.5 space-y-1 text-[#0A0A0A]">
                {lastResult.results.map((r) => (
                  <li key={r.email} className="flex items-center justify-between gap-2 flex-wrap" data-testid={`invite-result-${r.email}`}>
                    <span className="min-w-0 truncate">
                      <span className="font-mono">{r.email}</span> · <span className="capitalize">{r.status}</span>
                      {r.reason ? <span className="text-[#475569]"> ({r.reason})</span> : null}
                    </span>
                    {r.accept_url && (
                      <button
                        type="button"
                        onClick={async () => {
                          await copyRow(r.accept_url);
                          setCopiedResult(r.email);
                          setTimeout(() => setCopiedResult(""), 2500);
                        }}
                        data-testid={`invite-result-copy-${r.email}`}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] font-bold text-[#1E4D55] hover:text-[#163C42]"
                      >
                        {copiedResult === r.email ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy link</>}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Existing invites */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div role="tablist" className="inline-flex border border-[#0A0A0A]/15 bg-white" data-testid="admin-invite-status-tabs">
          {["pending", "accepted", "revoked", ""].map((f) => (
            <button
              key={f || "all"}
              role="tab"
              aria-selected={filter === f}
              data-testid={`admin-invite-status-${f || "all"}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.14em] font-bold ${
                filter === f ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F5F5F5]"
              }`}
            >
              {f || "All"}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={loadInvites}
          data-testid="admin-invite-refresh"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] font-bold text-[#1E4D55]"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#475569] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading invites…</div>
      ) : invites.length === 0 ? (
        <div className="bg-white border border-dashed border-[#0A0A0A]/15 p-6 text-sm text-[#475569]" data-testid="admin-invite-empty">
          No invites in this bucket.
        </div>
      ) : (
        <div className="bg-white border border-[#0A0A0A]/10" data-testid="admin-invite-list">
          {invites.map((i) => (
            <div key={i.id} data-testid={`invite-row-${i.id}`} className="px-4 py-3 border-b last:border-b-0 border-[#0A0A0A]/10 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <p className="font-semibold text-[#0A0A0A] text-sm truncate">{i.name || i.email}</p>
                <p className="text-xs text-[#475569] truncate">
                  {i.email} · invited by {i.invited_by_email || "—"} · {i.created_at ? new Date(i.created_at).toLocaleDateString("en-GB") : ""}
                </p>
              </div>
              <span data-testid={`invite-status-${i.id}`} className={`text-[10px] uppercase tracking-[0.14em] font-bold border px-1.5 py-0.5 ${badgeCls(i.status)}`}>
                {i.status}
              </span>
              {i.status === "pending" && (
                <>
                  <button
                    onClick={() => resend(i.id)}
                    data-testid={`invite-copy-link-${i.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E4D55] hover:text-[#163C42] px-2 py-1"
                    title="Rotate the token, copy a fresh link, and re-send the email"
                  >
                    {copiedId === i.id ? <><Check size={13} /> Copied &amp; sent</> : <><Copy size={13} /> Copy link &amp; resend</>}
                  </button>
                  <button
                    onClick={() => revoke(i.id)}
                    data-testid={`invite-revoke-${i.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#B91C1C] hover:text-[#7f1d1d] px-2 py-1"
                  >
                    <XCircle size={13} /> Revoke
                  </button>
                </>
              )}
              {linkFor?.id === i.id && linkFor.url && (
                <div className="w-full mt-2 bg-[#F5F5F5] border border-[#0A0A0A]/10 p-2 text-[11px] font-mono break-all text-[#0A0A0A]" data-testid={`invite-link-inline-${i.id}`}>
                  {linkFor.url}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
